import { defaultTo } from 'lodash';
import React, { useMemo } from 'react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { P200 } from '@atlaskit/theme/colors';
import { DynamicResultTable, SimpleTD } from '@css-xp/base';
import { TermDetailsAccountConnection } from '@css-xp/graphtypes/src/generated-types-new';
import { TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { NOT_AVAILABLE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';

interface Props {
  isLoading: boolean;
  associatedAccounts: TermDetailsAccountConnection | undefined | null;
}

const columnHeaders = [
  {
    content: 'Company name',
    width: 30,
  },
  {
    content: 'Billing account ID',
    width: 60,
  },
  {
    content: 'Credit limit consumed',
    width: 30,
  },
];

export const TermDetailsAssociatedAccountsTable: React.FC<Props> = ({ isLoading, associatedAccounts }) => {
  const headData = useMemo(() => {
    const cells = columnHeaders;
    return {
      cells,
    };
  }, []);

  const emptyStateOrderProp = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };

  const getRows = () => {
    return (associatedAccounts?.nodes ?? []).map((item) => {
      return {
        cells: [
          {
            content: item?.transactionAccountNumber ? (
              <a href={`${TRANSACTION_ACCOUNT_PATH_2}/${item.transactionAccountNumber}`}>
                <SimpleTD text={item?.customerName ? item.customerName : 'Billing account'} />
              </a>
            ) : (
              <SimpleTD text={item?.customerName ? item.customerName : 'Billing account'} />
            ),
          },
          {
            content: item?.transactionAccountNumber ? (
              <SimpleTD text={item.transactionAccountNumber} />
            ) : (
              <span aria-label="Not available" data-testid="not-available">
                {NOT_AVAILABLE}
              </span>
            ),
          },
          {
            content:
              item?.creditOutstanding !== undefined && item?.creditOutstanding !== null ? (
                <SimpleTD text={`USD ${item?.creditOutstanding}`} />
              ) : (
                <span aria-label="Not available" data-testid="not-available">
                  {NOT_AVAILABLE}
                </span>
              ),
          },
        ],
      };
    });
  };

  return (
    <>
      <DynamicResultTable
        isLoading={isLoading}
        headingText="Associated billing accounts"
        head={headData}
        rows={getRows()}
        emptyStateProps={emptyStateOrderProp}
      />
      <div className="associated-account-info">
        <InfoIcon label="" size="small" primaryColor={P200} />
        {
          'Net-30 payment terms have multiple associated billing accounts and the credits utilised across them are highlighted above'
        }
      </div>
    </>
  );
};

export default TermDetailsAssociatedAccountsTable;
